// Access Management Constants
// This file contains all role definitions and permission mappings
// Modify this file to easily change what each role can access

// Role definitions mapping backend IDs to frontend roles
export const ROLES = {
  LEVEL_1: 'a63e5c03-71a6-47ee-b490-480cc7aa3b4f',
  LEVEL_2: '414e738e-feaf-4aa4-854e-445bb77088ce',
  LEVEL_3: '7cb57348-ac9c-4110-a036-cc09f246956c',
  LEVEL_4: 'fbbedac1-d70f-4a24-842e-d910f471e218',
} as const;

// Permission categories - add new permissions here
export const PERMISSIONS = {
  // User Management
  MANAGE_USERS: 'manage_users',
  EDIT_OWN_PROFILE: 'edit_own_profile',
  VIEW_USER_LIST: 'view_user_list',
  // Primary Admin Only User Management
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',

  // Project Management
  CREATE_PROJECTS: 'create_projects',
  EDIT_PROJECTS: 'edit_projects',
  DELETE_PROJECTS: 'delete_projects',
  VIEW_PROJECTS: 'view_projects',
  ADD_PROJECT_MEMBERS: 'add_project_members',
  REMOVE_PROJECT_MEMBERS: 'remove_project_members',

  // Assembly & Items Management
  CREATE_GLOBAL_ASSEMBLIES: 'create_global_assemblies',
  EDIT_GLOBAL_ASSEMBLIES: 'edit_global_assemblies',
  DELETE_GLOBAL_ASSEMBLIES: 'delete_global_assemblies',
  VIEW_GLOBAL_ASSEMBLIES: 'view_global_assemblies',
  CREATE_TAKEOFF_ASSEMBLIES: 'create_takeoff_assemblies',
  EDIT_TAKEOFF_ASSEMBLIES: 'edit_takeoff_assemblies',
  DELETE_TAKEOFF_ASSEMBLIES: 'delete_takeoff_assemblies',
  VIEW_TAKEOFF_ASSEMBLIES: 'view_takeoff_assemblies',
  CREATE_ITEMS: 'create_items',
  EDIT_ITEMS: 'edit_items',
  DELETE_ITEMS: 'delete_items',
  VIEW_ITEMS: 'view_items',

  // Takeoff & Estimating
  PERFORM_TAKEOFFS: 'perform_takeoffs',
  EDIT_ESTIMATES: 'edit_estimates',
  VIEW_ESTIMATES: 'view_estimates',
  DELETE_ESTIMATES: 'delete_estimates',
  CREATE_ESTIMATES: 'create_estimates',

  // Closeout & Contracts
  MANAGE_CLOSEOUTS: 'manage_closeouts',
  VIEW_CLOSEOUTS: 'view_closeouts',
  MANAGE_CONTRACTS: 'manage_contracts',
  VIEW_CONTRACTS: 'view_contracts',
  MANAGE_CHANGE_ORDERS: 'manage_change_orders',
  VIEW_CHANGE_ORDERS: 'view_change_orders',
  CREATE_CHANGE_ORDERS: 'create_change_orders',
  EDIT_CHANGE_ORDERS: 'edit_change_orders',
  DELETE_CHANGE_ORDERS: 'delete_change_orders',
  VIEW_PROPRIETARY_DATA: 'view_proprietary_data',

  // System Administration (PRIMARY ADMIN ONLY - not assigned to any role)
  MANAGE_SUBSCRIPTIONS: 'manage_subscriptions',
  SYSTEM_SETTINGS: 'system_settings',
  VIEW_ANALYTICS: 'view_analytics',
  MANAGE_ROLES: 'manage_roles',

  // Document Management
  UPLOAD_DOCUMENTS: 'upload_documents',
  VIEW_DOCUMENTS: 'view_documents',
  DELETE_DOCUMENTS: 'delete_documents',
  ANNOTATE_DOCUMENTS: 'annotate_documents',

  // Drawing Tools
  USE_DRAWING_TOOLS: 'use_drawing_tools',
  EDIT_ANNOTATIONS: 'edit_annotations',
  DELETE_ANNOTATIONS: 'delete_annotations',
} as const;

// Role-Permission Matrix - MODIFY THIS TO CHANGE WHAT EACH ROLE CAN ACCESS
export const ROLE_PERMISSIONS: Record<string, readonly string[]> = {
  [ROLES.LEVEL_1]: [
    // Cheif Estimator Level
    PERMISSIONS.EDIT_OWN_PROFILE,
    PERMISSIONS.VIEW_USER_LIST,
    PERMISSIONS.ADD_PROJECT_MEMBERS,
    PERMISSIONS.REMOVE_PROJECT_MEMBERS,
    PERMISSIONS.CREATE_PROJECTS,
    PERMISSIONS.EDIT_PROJECTS,
    PERMISSIONS.DELETE_PROJECTS,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.CREATE_GLOBAL_ASSEMBLIES,
    PERMISSIONS.EDIT_GLOBAL_ASSEMBLIES,
    PERMISSIONS.DELETE_GLOBAL_ASSEMBLIES,
    PERMISSIONS.VIEW_GLOBAL_ASSEMBLIES,
    PERMISSIONS.CREATE_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.EDIT_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.DELETE_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.VIEW_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.CREATE_ITEMS,
    PERMISSIONS.EDIT_ITEMS,
    PERMISSIONS.DELETE_ITEMS,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.PERFORM_TAKEOFFS,
    PERMISSIONS.EDIT_ESTIMATES,
    PERMISSIONS.VIEW_ESTIMATES,
    PERMISSIONS.DELETE_ESTIMATES,
    PERMISSIONS.CREATE_ESTIMATES,
    PERMISSIONS.MANAGE_CLOSEOUTS,
    PERMISSIONS.VIEW_CLOSEOUTS,
    PERMISSIONS.MANAGE_CONTRACTS,
    PERMISSIONS.VIEW_CONTRACTS,
    PERMISSIONS.MANAGE_CHANGE_ORDERS,
    PERMISSIONS.VIEW_CHANGE_ORDERS,
    PERMISSIONS.UPLOAD_DOCUMENTS,
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.DELETE_DOCUMENTS,
    PERMISSIONS.ANNOTATE_DOCUMENTS,
    PERMISSIONS.USE_DRAWING_TOOLS,
    PERMISSIONS.EDIT_ANNOTATIONS,
    PERMISSIONS.DELETE_ANNOTATIONS,
    PERMISSIONS.VIEW_PROPRIETARY_DATA,
  ],

  [ROLES.LEVEL_2]: [
    // Estimator Level
    PERMISSIONS.EDIT_OWN_PROFILE,
    PERMISSIONS.ADD_PROJECT_MEMBERS,
    PERMISSIONS.REMOVE_PROJECT_MEMBERS,
    PERMISSIONS.CREATE_PROJECTS,
    PERMISSIONS.EDIT_PROJECTS,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_GLOBAL_ASSEMBLIES,
    PERMISSIONS.CREATE_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.EDIT_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.DELETE_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.VIEW_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.PERFORM_TAKEOFFS,
    PERMISSIONS.EDIT_ESTIMATES,
    PERMISSIONS.VIEW_ESTIMATES,
    PERMISSIONS.CREATE_ESTIMATES,
    PERMISSIONS.MANAGE_CLOSEOUTS,
    PERMISSIONS.VIEW_CLOSEOUTS,
    PERMISSIONS.VIEW_CONTRACTS,
    PERMISSIONS.CREATE_CHANGE_ORDERS,
    PERMISSIONS.VIEW_CHANGE_ORDERS,
    PERMISSIONS.UPLOAD_DOCUMENTS,
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.ANNOTATE_DOCUMENTS,
    PERMISSIONS.USE_DRAWING_TOOLS,
    PERMISSIONS.EDIT_ANNOTATIONS,
    PERMISSIONS.DELETE_ANNOTATIONS,
    PERMISSIONS.VIEW_PROPRIETARY_DATA,
  ],

  [ROLES.LEVEL_3]: [
    // Production Manager Level
    PERMISSIONS.EDIT_OWN_PROFILE,
    PERMISSIONS.CREATE_PROJECTS,
    PERMISSIONS.EDIT_PROJECTS,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_GLOBAL_ASSEMBLIES,
    PERMISSIONS.VIEW_TAKEOFF_ASSEMBLIES,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.PERFORM_TAKEOFFS,
    PERMISSIONS.VIEW_ESTIMATES,
    PERMISSIONS.VIEW_CLOSEOUTS,
    PERMISSIONS.VIEW_CONTRACTS,
    PERMISSIONS.CREATE_CHANGE_ORDERS,
    PERMISSIONS.EDIT_CHANGE_ORDERS,
    PERMISSIONS.VIEW_CHANGE_ORDERS,
    PERMISSIONS.UPLOAD_DOCUMENTS,
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.ANNOTATE_DOCUMENTS,
    PERMISSIONS.USE_DRAWING_TOOLS,
    PERMISSIONS.EDIT_ANNOTATIONS,
    PERMISSIONS.DELETE_ANNOTATIONS,
    PERMISSIONS.VIEW_PROPRIETARY_DATA,
  ],

  [ROLES.LEVEL_4]: [
    // Production Employee Level
    PERMISSIONS.EDIT_OWN_PROFILE,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.ANNOTATE_DOCUMENTS,
    PERMISSIONS.USE_DRAWING_TOOLS,
    PERMISSIONS.EDIT_ANNOTATIONS,
    PERMISSIONS.DELETE_ANNOTATIONS,
  ],
} as const;

// Role display names
export const ROLE_NAMES: Record<string, string> = {
  [ROLES.LEVEL_1]: 'Level 1 - Chief Estimator',
  [ROLES.LEVEL_2]: 'Level 2 - Estimator',
  [ROLES.LEVEL_3]: 'Level 3 - Production Manager',
  [ROLES.LEVEL_4]: 'Level 4 - Production Employee',
} as const;

// Permission descriptions for UI tooltips/help
export const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  [PERMISSIONS.MANAGE_USERS]: 'Create, edit, and delete user accounts',
  [PERMISSIONS.EDIT_OWN_PROFILE]: 'Edit your own user profile and settings',
  [PERMISSIONS.VIEW_USER_LIST]: 'View the list of all users in the account',
  [PERMISSIONS.CREATE_USERS]: 'Create new user accounts',
  [PERMISSIONS.EDIT_USERS]: 'Edit existing user accounts',
  [PERMISSIONS.DELETE_USERS]: 'Delete user accounts',
  [PERMISSIONS.CREATE_PROJECTS]: 'Create new projects',
  [PERMISSIONS.EDIT_PROJECTS]: 'Edit existing projects',
  [PERMISSIONS.DELETE_PROJECTS]: 'Delete projects',
  [PERMISSIONS.VIEW_PROJECTS]: 'View project details and lists',
  [PERMISSIONS.CREATE_GLOBAL_ASSEMBLIES]: 'Create new glovbassemblies',
  [PERMISSIONS.EDIT_GLOBAL_ASSEMBLIES]: 'Edit existing global assemblies',
  [PERMISSIONS.DELETE_GLOBAL_ASSEMBLIES]: 'Delete global assemblies',
  [PERMISSIONS.VIEW_GLOBAL_ASSEMBLIES]: 'View global assembly details and lists',
  [PERMISSIONS.CREATE_TAKEOFF_ASSEMBLIES]: 'Create new takeoff assemblies',
  [PERMISSIONS.EDIT_TAKEOFF_ASSEMBLIES]: 'Edit existing takeoff assemblies',
  [PERMISSIONS.DELETE_TAKEOFF_ASSEMBLIES]: 'Delete takeoff assemblies',
  [PERMISSIONS.VIEW_TAKEOFF_ASSEMBLIES]: 'View takeoff assembly details and lists',
  [PERMISSIONS.CREATE_ITEMS]: 'Create new items',
  [PERMISSIONS.EDIT_ITEMS]: 'Edit existing items',
  [PERMISSIONS.DELETE_ITEMS]: 'Delete items',
  [PERMISSIONS.VIEW_ITEMS]: 'View item details and lists',
  [PERMISSIONS.PERFORM_TAKEOFFS]: 'Perform takeoff measurements and calculations',
  [PERMISSIONS.EDIT_ESTIMATES]: 'Edit existing estimates',
  [PERMISSIONS.VIEW_ESTIMATES]: 'View estimate details and lists',
  [PERMISSIONS.DELETE_ESTIMATES]: 'Delete estimates',
  [PERMISSIONS.CREATE_ESTIMATES]: 'Create new estimates',
  [PERMISSIONS.MANAGE_CLOSEOUTS]: 'Manage closeout processes and data',
  [PERMISSIONS.VIEW_CLOSEOUTS]: 'View closeout information',
  [PERMISSIONS.MANAGE_CONTRACTS]: 'Manage contracts and agreements',
  [PERMISSIONS.VIEW_CONTRACTS]: 'View contract information',
  [PERMISSIONS.MANAGE_CHANGE_ORDERS]: 'Manage change orders',
  [PERMISSIONS.VIEW_CHANGE_ORDERS]: 'View change order information',
  [PERMISSIONS.CREATE_CHANGE_ORDERS]: 'Create new change orders',
  [PERMISSIONS.EDIT_CHANGE_ORDERS]: 'Edit existing change orders',
  [PERMISSIONS.DELETE_CHANGE_ORDERS]: 'Delete change orders',
  [PERMISSIONS.VIEW_PROPRIETARY_DATA]: 'View proprietary and sensitive data',
  [PERMISSIONS.MANAGE_SUBSCRIPTIONS]: 'Manage account subscriptions and billing',
  [PERMISSIONS.SYSTEM_SETTINGS]: 'Access system-wide settings and configuration',
  [PERMISSIONS.VIEW_ANALYTICS]: 'View analytics and reporting data',
  [PERMISSIONS.MANAGE_ROLES]: 'Manage user roles and permissions',
  [PERMISSIONS.UPLOAD_DOCUMENTS]: 'Upload documents to projects',
  [PERMISSIONS.VIEW_DOCUMENTS]: 'View project documents',
  [PERMISSIONS.DELETE_DOCUMENTS]: 'Delete project documents',
  [PERMISSIONS.ANNOTATE_DOCUMENTS]: 'Add annotations to documents',
  [PERMISSIONS.USE_DRAWING_TOOLS]: 'Use drawing and measurement tools',
  [PERMISSIONS.EDIT_ANNOTATIONS]: 'Edit existing annotations',
  [PERMISSIONS.DELETE_ANNOTATIONS]: 'Delete annotations',
} as const;
